/* global Popper */
/**
 * Modifiers builder.
 *
 * For more examples of custom modifiers and their usage you can check these links:
 * - https://codesandbox.io/p/sandbox/great-tesla-3roz7?file=%2Fsrc%2Findex.js%3A12%2C4
 * - https://codesandbox.io/p/sandbox/bitter-sky-pe3z9?file=%2Fsrc%2Findex.js
 */
export default class PopperModifiers {
  constructor() {
    this._modifiers = [];
  }

  toArray() {
    return this._modifiers;
  }

  get appliedModifierNames() {
    return this._modifiers.map((modifier) => modifier.name);
  }

  /**
   * @see https://popper.js.org/docs/v2/modifiers/offset/
   * @param {[Number, Number]} value
   * @return {PopperModifiers}
   */
  offset(value) {
    this._modifiers.push({
      name: 'offset',
      options: {
        offset: value,
      },
    });
    return this;
  }

  /**
   * @see https://popper.js.org/docs/v2/modifiers/flip/#padding
   * @param {Number} padding
   * @return {PopperModifiers}
   */
  flip(padding) {
    this._modifiers.push({
      name: 'flip',
      options: {
        padding,
      },
    });
    return this;
  }

  /**
   * @see https://popper.js.org/docs/v2/modifiers/prevent-overflow/#padding
   * @see https://popper.js.org/docs/v2/utils/detect-overflow/#boundary
   * @param {Number} padding
   * @param {Element} [boundary]
   * @return {PopperModifiers}
   */
  preventOverflow(padding, boundary) {
    this._modifiers.push({
      name: 'preventOverflow',
      options: {
        padding,
        ...(boundary && { boundary }),
      },
    });
    return this;
  }

  /**
   * Custom modifier relying on maxSize. Allows container to not exceed (or follow) the closest container width.
   * What woodoo magic Popper uses to automatically identify such containers is unknown, but by the time being,
   * it worked fine.
   * @see https://popper.js.org/docs/v2/modifiers/community-modifiers/
   * @param {Number} padding
   * @return {PopperModifiers}
   */
  inlineLimit(padding) {
    if (!this.appliedModifierNames.includes('maxSize')) {
      this.#maxSize(padding);
    }
    this._modifiers.push({
      name: 'inlineLimit',
      enabled: true,
      phase: 'beforeWrite',
      requires: ['maxSize'],
      fn: ({ state }) => {
        state.styles.popper.maxWidth = `${state.modifiersData.maxSize.width}px`;
      },
    });
    return this;
  }

  /**
   * Same as {@link inlineLimit}, but for height instead of width.
   * @see inlineLimit
   * @param {Number} padding
   * @return {PopperModifiers}
   */
  blockLimit(padding) {
    if (!this.appliedModifierNames.includes('maxSize')) {
      this.#maxSize(padding);
    }
    this._modifiers.push({
      name: 'blockLimit',
      enabled: true,
      phase: 'beforeWrite',
      requires: ['maxSize'],
      fn: ({ state }) => {
        state.styles.popper.maxHeight = `${state.modifiersData.maxSize.height}px`;
      },
    });
    return this;
  }

  /**
   * @see https://github.com/atomiks/popper-max-size-modifier
   * @param {Number} padding
   */
  #maxSize(padding) {
    this._modifiers.push({
      name: 'maxSize',
      enabled: true,
      phase: 'main',
      requiresIfExists: ['offset', 'preventOverflow', 'flip'],
      fn: ({ state, name }) => {
        const overflow = Popper.detectOverflow(state, { padding });
        const { x, y } = state.modifiersData.preventOverflow || { x: 0, y: 0 };
        const { width, height } = state.rects.popper;
        const [basePlacement] = state.placement.split('-');

        const widthProp = basePlacement === 'left' ? 'left' : 'right';
        const heightProp = basePlacement === 'top' ? 'top' : 'bottom';

        state.modifiersData[name] = {
          width: width - overflow[widthProp] - x,
          height: height - overflow[heightProp] - y,
        };
      },
    });
  }
}
